import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

declare const require: (path: string) => any;

/** Minimal shape; package typings are incompatible with this project's TS version. */
interface MixpanelLike {
  init(token: string, config?: object): void;
  identify(id: string): void;
  reset(): void;
  track(eventName: string, props?: object): void;
  register(props: object): void;
  people: { set(props: object): void };
}

/**
 * Webpack bundles mixpanel-browser as an ES module; `require()` often yields `{ default: api }`.
 */
function loadMixpanel(): MixpanelLike {
  // tslint:disable-next-line:no-var-requires
  const mpModule = require('mixpanel-browser');
  if (mpModule && typeof mpModule.init === 'function') {
    return mpModule as MixpanelLike;
  }
  if (mpModule && mpModule.default && typeof mpModule.default.init === 'function') {
    return mpModule.default as MixpanelLike;
  }
  return mpModule as MixpanelLike;
}

const mixpanel = loadMixpanel();
/**
 * Vision Mixpanel: page views + user identity (email, name) after login.
 */
@Injectable()
export class MixpanelService {
  private initialized = false;

  init(): void {
    if (this.initialized) {
      return;
    }
    const key = environment.mixpanelProjectKey;
    if (!key) {
      return;
    }
    mixpanel.init(key, {
      debug: !environment.production,
      persistence: 'localStorage'
    });
    mixpanel.register({
      app: 'vision',
      app_version: environment.version || 'unknown'
    });
    this.initialized = true;
  }

  /**
   * Call after successful login or when restoring a session from localStorage.
   */
  identifyUser(contactId: number, email: string, displayName: string): void {
    if (!email || !contactId) {
      return;
    }
    this.init();
    if (!this.initialized) {
      return;
    }
    const distinctId = String(contactId);
    mixpanel.identify(distinctId);
    const name =
      (displayName && displayName.trim()) ||
      email;
    mixpanel.people.set({
      $email: email,
      $name: name,
      contact_id: contactId
    });
  }

  /**
   * On navigation end; attributes page views to the identified user when logged in.
   */
  trackPageView(url: string): void {
    this.init();
    if (!this.initialized || !url) {
      return;
    }
    mixpanel.track('Page View', {
      path: url,
      app: 'vision'
    });
  }

  /**
   * Restore Mixpanel identity when the SPA reloads with an existing session.
   */
  restoreIdentityFromStorage(): void {
    const token = localStorage.getItem('Token');
    const cid = localStorage.getItem('LoggedContactId');
    const email = localStorage.getItem('UserName');
    if (!token || !cid || !email) {
      return;
    }
    const nameFromStorage = localStorage.getItem('UserName1');
    this.identifyUser(parseInt(cid, 10), email, nameFromStorage || email);
  }

  reset(): void {
    if (!this.initialized) {
      return;
    }
    mixpanel.reset();
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/mixpanel/mixpanel.service.ts